<template>
    <div>
        <Toast />
        <div class="card">
            <DataTable
                :value="tableData"
                showGridlines
                stripedRows
                :rows="rows"
                :totalRecords="totalRecords"
                :currentPage="currentPage"
                :paginator="true"
                :loading="loading"
                @page="onPaginate"
                :rowsPerPageOptions="[10, 25, 100]"
                tableStyle="min-width: 50rem"
                lazy
            >
                <template #header>
                    <div class="search-container">
                        <span class="p-input-icon-left">
                          <i class="pi pi-search" />
                          <InputText
                              v-model="criteria"
                              placeholder="Rechercher par IP"
                              @keyup.enter="onFilterDt"
                              @input="onClearInputFilter"
                          />
                        </span>
                    </div>
                </template>
                <Column field="ipAddress" header="Adresse IP whitelistée"></Column>
                <Column field='company.name' header="Société"></Column>
                <Column field="requestedBy" header="Demandé par"></Column>
                <Column field="description" header="Description"></Column>
                <Column field="createdAt" header="Date d'ajout">
                    <template #body="slotProps">
                        {{ formatDate(slotProps.data.createdAt) }}
                    </template>
                </Column>
                <Column header="Ajouté par">
                    <template #body="slotProps">
                        {{ slotProps.data.createdBy.firstname }} {{ slotProps.data.createdBy.lastname }}
                    </template>
                </Column>
                <Column field="status" header="Statut">
                    <template #body="slotProps">
                        <Tag :value="slotProps.data.status ? 'Actif' : 'Inactif'" :severity="slotProps.data.status ? 'success' : 'danger'" rounded />
                    </template>
                </Column>
                <Column>
                    <template #header>
                        <div>Actions</div>
                    </template>
                    <template #body="slotProps">
                        <InputSwitch v-model="slotProps.data.status" @change="() => handleStatusChange(slotProps.data)" />
                        <Button @click="() => confirmDeleteDialog(slotProps.data)" type="button" title="Supprimer" icon="pi pi-trash" class="ml-1 p-button-danger"></Button>
                    </template>
                </Column>
                <template #empty>Aucun résultat trouvé.</template>
            </DataTable>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { fetchIpWhitelists, toggleStatus, deleteIP } from '@/_services/securite/whitelist/ipWhitelistService';
import { IpWhitelist } from "@/models/securite/whitelist/IpWhiteList";
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import InputSwitch from "primevue/inputswitch";
import Swal from '@/_helpers/swal';
const tableData = ref<IpWhitelist[]>([]);
const currentPage = ref<number>(0);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const loading = ref<boolean>(true);
const page = ref<number>(1);
const criteria = ref<string>("");
const toast = useToast();
const confirm = useConfirm();

async function populateDatatable() {
    loading.value = true;
    const [loadingDt, totalRecordsDt, tableDataDt] = await fetchIpWhitelists(rows.value, page.value, criteria.value.trim());
    loading.value = loadingDt.value;
    tableData.value = tableDataDt.value;
    totalRecords.value = totalRecordsDt.value;

    return [loading.value, totalRecords.value, tableData.value]
}

onMounted(async () => {
    await populateDatatable();
});

const onPaginate = async (e) => {
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;
    populateDatatable();
};

const formatDate = (dateString) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

const onFilterDt = async () => {
    rows.value = 10;
    page.value = 1;
    populateDatatable();
};

const onClearInputFilter = () => {
    if (criteria.value === "") {
        rows.value = 10;
        page.value = 1;
        populateDatatable();
    }
};

const handleStatusChange = async (data: IpWhitelist) => {
    try {
        await toggleStatus(data);
        toast.add({ severity: 'success', summary: 'Success', detail: 'Le statut a bien été modifié !', life: 3000 });
        await populateDatatable();
    }  catch (error) {
        let errorMessage = 'Erreur inconnue.';
        if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
        } else if (error.message) {
            errorMessage = error.message;
        }
        toast.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 });
    }
};
const confirmDeleteDialog = (ip: IpWhitelist) => {
    confirm.require({
        group: 'global',
        message: 'Êtes-vous sûr de vouloir supprimer cette adresse IP ?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: "Oui",
        rejectLabel: "Non",
        acceptClass: 'p-button-danger',
        defaultFocus: "reject",
        accept: async () => {
            try {
                await deleteIP(ip.uuid);
                toast.add({ severity: 'success', summary: 'Supprimé', detail:  `L'adresse IP a bien été supprimée`, life: 3000 });
                await populateDatatable();
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Erreur de suppression', detail: `Échec de la suppression de l'adresse IP `, life: 3000 });
            }
        },
    });
};
</script>

<style scoped>
.confirmation-content i {
    margin-right: 0.5rem;
}

.confirmation-content span {
    font-size: 1.2rem;
}
</style>
